
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { JobSearchResult } from '@/types/jobSearch';
import { toast } from 'sonner';

interface UseJobProcessingProps {
  searchResults: JobSearchResult[];
  setSearchResults: React.Dispatch<React.SetStateAction<JobSearchResult[]>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
}

export const useJobProcessing = ({ searchResults, setSearchResults, setError }: UseJobProcessingProps) => {
  const [crawlingJobId, setCrawlingJobId] = useState<string | null>(null);
  const [validatingJobId, setValidatingJobId] = useState<string | null>(null);

  const crawlJobDetails = async (jobId: string) => {
    setCrawlingJobId(jobId);
    setError(null);
    
    const jobBeforeCrawl = searchResults.find(job => job.id === jobId);
    const originalStatus = jobBeforeCrawl ? jobBeforeCrawl.status : 'new';

    try {
      setSearchResults(prev => prev.map(job => job.id === jobId ? { ...job, status: 'crawling' } : job));

      const { data, error: functionError } = await supabase.functions.invoke('crawl-job-details', {
        body: { search_result_id: jobId },
      });

      if (functionError) throw functionError;
      if (data.error) throw new Error(data.error);

      setSearchResults(prev => prev.map(job => job.id === jobId ? { ...job, ...data.updatedJob } : job));
      toast.success(`Successfully started crawling details for job ID: ${jobId}`);

    } catch (e: any) {
      console.error('Error crawling job details:', e);
      const errorMessage = e.message || 'Failed to crawl job details.';
      setError(errorMessage);
      toast.error(errorMessage);
      setSearchResults(prev => prev.map(job => {
        if (job.id === jobId) {
          return { ...job, status: originalStatus === 'crawling' ? 'error' : originalStatus };
        }
        return job;
      }));
    } finally {
      setCrawlingJobId(null);
    }
  };
  
  const validateJobData = async (jobId: string) => {
    setValidatingJobId(jobId);
    setError(null);

    const jobBeforeValidation = searchResults.find(job => job.id === jobId);
    const originalStatus = jobBeforeValidation ? jobBeforeValidation.status : 'crawled';

    try {
      setSearchResults(prev => prev.map(job => job.id === jobId ? { ...job, status: 'validation_pending' } : job));

      const { data, error: functionError } = await supabase.functions.invoke('verify-job-data', {
        body: { search_result_id: jobId }, 
      });

      if (functionError) throw functionError;
      if (data.error) throw new Error(data.error);
      
      setSearchResults(prev => prev.map(job => job.id === jobId ? { ...job, ...data.updatedJob } : job));
      toast.success(`Successfully started validation for job ID: ${jobId}`);

    } catch (e: any)      {
      console.error('Error validating job data:', e);
      const errorMessage = e.message || 'Failed to validate job data.';
      setError(errorMessage);
      toast.error(errorMessage);
      setSearchResults(prev => prev.map(job => {
        if (job.id === jobId) {
          return { ...job, status: originalStatus === 'validation_pending' ? 'error' : originalStatus };
        }
        return job;
      }));
    } finally {
      setValidatingJobId(null);
    }
  };

  return {
    crawlJobDetails,
    validateJobData,
    crawlingJobId,
    validatingJobId,
  };
};
